import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormFileUpload } from '@ipfabric/ui-components/FormFileUpload/FormFileUpload';
import { Label } from '@ipfabric/ui-components/Label/Label';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { Message } from '@ipfabric/ui-components/Message/Message';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { MAX_CSV_FILE_SIZE } from './aimCsvSchemas';
import { AIM_ENTITY_LABELS, type AimEntityKind, otherKinds } from './types';

type FileSlotValues = {
  applicationsFile: File | null;
  workloadsFile: File | null;
  flowsFile: File | null;
};

export type ImportSummary = {
  perEntity: Partial<
    Record<
      AimEntityKind,
      { imported: number; errors: { id: string; message: string }[] }
    >
  >;
  hasAnyImport: boolean;
};

type Props = {
  triggeredFrom: AimEntityKind;
  onDownloadExample: () => void;
  onImport: (
    files: FileSlotValues,
    mode: 'append' | 'replace',
  ) => Promise<ImportSummary>;
  onClose: () => void;
};

const VALID_MIME = ['text/csv', 'application/vnd.ms-excel'];

const formatBytes = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(2)} ${units[i]}`;
};

export const AimDataTransferForm = ({
  triggeredFrom,
  onDownloadExample,
  onImport,
  onClose,
}: Props) => {
  const labels = AIM_ENTITY_LABELS[triggeredFrom];
  const others = otherKinds(triggeredFrom);
  const main = triggeredFrom;
  const opt1 = others[0] as AimEntityKind;
  const opt2 = others[1] as AimEntityKind;

  const { control, formState, setError, clearErrors, setValue } =
    useCustomForm<FileSlotValues>({
      defaultValues: {
        applicationsFile: null,
        workloadsFile: null,
        flowsFile: null,
      },
      reValidateMode: 'onChange',
    });

  // Per-slot useWatch (not whole-form) so identity is stable when other
  // slots change; avoids re-running validation on every render and the
  // setError → re-render → effect → setError loop that would result.
  const applicationsFile = useWatch({ control, name: 'applicationsFile' });
  const workloadsFile = useWatch({ control, name: 'workloadsFile' });
  const flowsFile = useWatch({ control, name: 'flowsFile' });

  // File-type and file-size validation per slot (replicated from DataTransferForm
  // because we only reuse its low-level CSV helpers, not its UI).
  const validateSlot = useCallback(
    (slot: keyof FileSlotValues, file: File | null): void => {
      if (!file) {
        clearErrors(slot);
        return;
      }
      const okMime =
        VALID_MIME.includes(file.type) ||
        file.name.toLowerCase().endsWith('.csv');
      if (!okMime) {
        setError(slot, {
          type: 'fileType',
          message: 'The uploaded file is not a valid CSV file.',
        });
        return;
      }
      if (file.size > MAX_CSV_FILE_SIZE) {
        setError(slot, {
          type: 'fileSize',
          message: `File size exceeds the ${formatBytes(MAX_CSV_FILE_SIZE)} limit.`,
        });
        return;
      }
      clearErrors(slot);
    },
    [clearErrors, setError],
  );

  useEffect(() => {
    validateSlot('applicationsFile', applicationsFile);
  }, [applicationsFile, validateSlot]);
  useEffect(() => {
    validateSlot('workloadsFile', workloadsFile);
  }, [workloadsFile, validateSlot]);
  useEffect(() => {
    validateSlot('flowsFile', flowsFile);
  }, [flowsFile, validateSlot]);

  const fileMap: FileSlotValues = {
    applicationsFile,
    workloadsFile,
    flowsFile,
  };

  const [importLoading, setImportLoading] = useState<LoadingState>('INITIAL');
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);

  const slotName = (kind: AimEntityKind): keyof FileSlotValues =>
    AIM_ENTITY_LABELS[kind].fileSlotName as keyof FileSlotValues;

  const anyFileUploaded = Boolean(
    applicationsFile || workloadsFile || flowsFile,
  );
  const anyFileError =
    Boolean(formState.errors.applicationsFile) ||
    Boolean(formState.errors.workloadsFile) ||
    Boolean(formState.errors.flowsFile);
  const importDisabled =
    !anyFileUploaded || anyFileError || importLoading === 'LOADING';

  const handleImport = async (mode: 'append' | 'replace') => {
    try {
      const result = await withLoadingIndicator(setImportLoading)(() =>
        onImport(fileMap, mode),
      );
      setImportResult(result);
      // Clear the file slots after a successful (full or partial) import so
      // the user can stage another batch.
      setValue('applicationsFile', null);
      setValue('workloadsFile', null);
      setValue('flowsFile', null);
    } catch {
      setImportResult({
        perEntity: {},
        hasAnyImport: false,
      });
    }
  };

  return (
    <SidePanel
      title='Import data'
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            type='button'
            variant='primary'
            icon='fa-file-import'
            disabled={importDisabled}
            loading={importLoading === 'LOADING'}
            onClick={() => handleImport('append')}
          >
            Import
          </Button>
          <Button
            type='button'
            variant='primary'
            icon='fa-arrow-right-arrow-left'
            disabled={importDisabled}
            loading={importLoading === 'LOADING'}
            onClick={() => handleImport('replace')}
          >
            Import &amp; replace
          </Button>
          <Button type='button' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <div className='flex flex-col gap'>
        <form>
          <Card className='flex flex-col gap p-sm'>
            <div className='flex justify-between'>
              <Label renderAs='div' className='mb-3xs'>
                Data import
              </Label>
              <Link
                renderAs='button'
                type='button'
                accent='positive'
                size='default'
                onClick={onDownloadExample}
                className='self-start'
              >
                Download example files
              </Link>
            </div>
            <div className='text-plain text-sm'>
              You can add more {labels.entityPlural.toLowerCase()} (or replace
              existing data completely) using CSV files. Download example files
              to see how they should be structured.
            </div>
            <FormFileUpload
              control={control}
              name={slotName(main)}
              key={fileMap[slotName(main)]?.name ?? `${main}-empty`}
              className='w-full'
              placeholder={`${AIM_ENTITY_LABELS[main].entityPlural} file`}
            />

            <div>
              <Label renderAs='div' className='mb-3xs'>
                Optional files
              </Label>
              <div className='text-plain text-sm'>
                Optionally, you can also add{' '}
                {AIM_ENTITY_LABELS[opt1].entityPlural.toLowerCase()} and{' '}
                {AIM_ENTITY_LABELS[opt2].entityPlural.toLowerCase()} (or replace
                existing data completely) along with the{' '}
                {labels.entityPlural.toLowerCase()}.
              </div>
            </div>
            <FormFileUpload
              control={control}
              name={slotName(opt1)}
              key={fileMap[slotName(opt1)]?.name ?? `${opt1}-empty`}
              className='w-full'
              placeholder={`${AIM_ENTITY_LABELS[opt1].entityPlural} file`}
            />
            <FormFileUpload
              control={control}
              name={slotName(opt2)}
              key={fileMap[slotName(opt2)]?.name ?? `${opt2}-empty`}
              placeholder={`${AIM_ENTITY_LABELS[opt2].entityPlural} file`}
            />
          </Card>
        </form>

        {(importLoading !== 'INITIAL' || importResult) && (
          <Card className='p'>
            {importLoading === 'LOADING' && (
              <Message
                title='Processing files'
                message='Importing data. Please wait...'
                loading
                variant='plain'
                type='plain'
              />
            )}
            {importResult && importLoading === 'LOADED' && (
              <ImportResultView summary={importResult} />
            )}
            {importLoading === 'ERROR' && !importResult && (
              <Message
                title='Import failed'
                message='An unexpected error occurred. Please try again.'
                type='error'
                variant='plain'
              />
            )}
          </Card>
        )}
      </div>
    </SidePanel>
  );
};

const ImportResultView = ({ summary }: { summary: ImportSummary }) => {
  const entries = Object.entries(summary.perEntity) as [
    AimEntityKind,
    { imported: number; errors: { id: string; message: string }[] },
  ][];
  const hasErrors = entries.some(([, v]) => v.errors.length > 0);
  const allFailed = !summary.hasAnyImport;

  const messageType: 'success' | 'warning' | 'error' = allFailed
    ? 'error'
    : hasErrors
      ? 'warning'
      : 'success';
  const title = allFailed
    ? 'Data not imported'
    : hasErrors
      ? 'Data imported partially'
      : 'Data imported successfully';

  return (
    <>
      <Message
        variant='plain'
        type={messageType}
        title={title}
        statusIcon
        message={
          <div className='flex flex-col gap-3xs'>
            {entries.map(([kind, v]) => (
              <div key={kind}>
                <b>{AIM_ENTITY_LABELS[kind].entityPlural}:</b> {v.imported}{' '}
                imported
                {v.errors.length > 0 ? `, ${v.errors.length} failed` : ''}
              </div>
            ))}
          </div>
        }
      />
      {entries.flatMap(([kind, v]) =>
        v.errors.map((e) => (
          <div
            key={`${kind}-${e.id}`}
            className='text-sm text-status-error mt-3xs'
          >
            <b>
              {AIM_ENTITY_LABELS[kind].entitySingular} {e.id}:
            </b>{' '}
            {e.message}
          </div>
        )),
      )}
    </>
  );
};
